import React, { useMemo, useCallback } from 'react';
import { useRole } from '../../context/roleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faEye,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import DataTable from '../common/DataTable';
import { useTranslation } from 'react-i18next';

const LocationsTable = ({
  locations = [],
  loading = false,
  selectedLocationIds = [],
  onSelectLocation,
  onSelectAll,
  onEditLocation,
  onDeleteLocation,
  onViewLocation,
  actionLoading = false,
}) => {
  const { checkDeleteAccess } = useRole();
  const { t } = useTranslation();
  const strings = useMemo(() => t('locations', { returnObjects: true }) || {}, [t]);

  const hasRows = Array.isArray(locations) && locations.length > 0;
  const selectedSet = useMemo(() => new Set(selectedLocationIds || []), [selectedLocationIds]);
  const allSelected = hasRows && (selectedLocationIds || []).length === locations.length;

  const hoverColor = useMemo(() => colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5), []);
  const selectedColor = useMemo(() => colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1), []);
  const selectionAccent = UI_COLORS.BUTTONS.PRIMARY.background;

  const renderActions = useCallback(
    (location) => (
      <div className="flex items-center justify-center space-x-2">
        <button
          onClick={() => onViewLocation?.(location.id)}
          disabled={actionLoading}
          className="p-2 rounded-lg transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            color: UI_COLORS.TEXT.PRIMARY,
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.25),
          }}
          onMouseEnter={(event) => {
            if (!actionLoading) event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.45);
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.25);
          }}
          title={strings.actions?.buttons?.view}
          aria-label={`${strings.actions?.buttons?.view} ${location.name}`}
        >
          <FontAwesomeIcon icon={faEye} className="text-sm" />
        </button>

        <button
          onClick={() => onEditLocation?.(location.id)}
          disabled={actionLoading}
          className="p-2 rounded-lg transition-all duration-150"
          style={{
            color: UI_COLORS.STATUS.WARNING,
            backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.08),
          }}
          onMouseEnter={(event) => {
            event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.14);
          }}
          onMouseLeave={(event) => {
            event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.08);
          }}
          title={strings.actions?.buttons?.edit}
          aria-label={`${strings.actions?.buttons?.edit} ${location.name}`}
        >
          <FontAwesomeIcon icon={faEdit} className="text-sm" />
        </button>

        {checkDeleteAccess() && (
          <button
            onClick={() => onDeleteLocation?.(location.id)}
            disabled={actionLoading}
            className="p-2 rounded-lg transition-all duration-150"
            style={{
              color: UI_COLORS.STATUS.ERROR,
              backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08),
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.14);
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.08);
            }}
            title={strings.actions?.buttons?.delete}
            aria-label={`${strings.actions?.buttons?.delete} ${location.name}`}
          >
            <FontAwesomeIcon icon={faTrash} className="text-sm" />
          </button>
        )}
      </div>
    ),
    [actionLoading, onViewLocation, onEditLocation, onDeleteLocation, strings, checkDeleteAccess]
  );

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        label: strings.table?.columns?.select,
        width: '60px',
        align: 'center',
        sortable: false,
        renderHeader: ({ loading: isLoading }) => (
          <input
            type="checkbox"
            aria-label={strings.table?.aria?.selectAll}
            checked={allSelected}
            onChange={() => onSelectAll?.(!allSelected)}
            disabled={!hasRows || isLoading}
            className="rounded focus:ring-2"
            style={{ accentColor: selectionAccent }}
          />
        ),
        renderCell: (location) => (
          <input
            type="checkbox"
            aria-label={`${strings.table?.aria?.selectRow || ''} ${location.name || ''}`}
            checked={selectedSet.has(location.id)}
            onChange={(e) => onSelectLocation?.(location.id, e.target.checked)}
            className="rounded focus:ring-2"
            style={{ accentColor: selectionAccent }}
          />
        ),
        skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
      },

      {
        id: 'country',
        label: strings.table?.columns?.country || 'Country',
        sortable: true,
        sortAccessor: 'country',
        renderCell: (location) => location.country,
        width: '140px',
      },
      {
        id: 'state',
        label: strings.table?.columns?.state,
        sortable: true,
        sortAccessor: 'state',
        renderCell: (location) => location.state,
        width: '140px',
      },
      {
        id: 'city',
        label: strings.table?.columns?.city,
        sortable: true,
        sortAccessor: 'city',
        renderCell: (location) => location.city,
        width: '140px',
      },
      {
        id: 'delivery_location',
        label: strings.table?.columns?.location,
        sortable: false,
        renderCell: (location) => (
          <div className="text-sm text-gray-900 truncate" title={location.delivery_location}>
            {location.delivery_location || ''}
          </div>
        ),
        width: '160px',
      },
      {
        id: 'map_url',
        label: strings.table?.columns?.map_url,
        sortable: false,
        renderCell: (location) => (
          location.map_url ? (
            <a href={location.map_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800">
              {strings.table?.copy?.viewMap}
            </a>
          ) : ''
        ),
        width: '140px',
      },
      {
        id: 'actions',
        label: strings.table?.columns?.actions,
        align: 'center',
        sortable: false,
        renderCell: renderActions,
        width: '160px',
      },
    ],
    // dependencies
    [allSelected, hasRows, selectionAccent, selectedSet, strings, renderActions, onSelectAll, onSelectLocation]
  );

  const getRowProps = useCallback(
    (location) => {
      const isSelected = selectedSet.has(location.id);
      return {
        className: 'hover:bg-opacity-50 transition-colors',
        style: { backgroundColor: isSelected ? selectedColor : 'transparent' },
        onMouseEnter: (event) => {
          if (!isSelected) event.currentTarget.style.backgroundColor = hoverColor;
        },
        onMouseLeave: (event) => {
          event.currentTarget.style.backgroundColor = isSelected ? selectedColor : 'transparent';
        },
      };
    },
    [hoverColor, selectedColor, selectedSet]
  );

  const emptyState = useMemo(
    () => ({
      title: strings?.messages?.noData || t('common.emptyStates.noData'),
      message: strings?.messages?.noDataLong || '',
    }),
    [strings, t]
  );

  return (
    <DataTable
      columns={columns}
      data={Array.isArray(locations) ? locations : []}
      loading={loading}
      keyField="id"
      enablePagination={false}
      defaultSort={{ field: 'country', direction: 'asc' }}
      getRowProps={getRowProps}
      emptyState={emptyState}
    />
  );
};

export default LocationsTable;
